import _ from 'lodash';
import {
  Container,
  Fade,
  IconButton,
} from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import useResponsive from '../../../../../utils/responsive';
import { Button, Map, PlacesSearch } from '../../../../common';
import Layout, { Main } from '../../Layout';
import configs from '../../../../../configs/configuration';
import ThailandAddressForm from './ThailandAddressForm';
import MalaysiaAddressForm from './MalaysiaAddressForm';
import { useLanguage } from '../../../../../context/language/language';
import useDeliveryData from '../../../../common/hook/useDeliveryData';
import useDeliveryControlFlow from '../../../../common/hook/useDeliveryControlFlow';
import { useSite } from '../../../../../context/siteContext';
import { defaultModalSize } from '../../modalSize';
import { trackPageLoad } from '../../../../../utils/adobeAnalytics';
import { interpretAddress } from '../../../../../utils/mapAddressUtils';
import { getImageURL } from '../../../../../utils/getImageURL';
import { setDeliveryAddressStep } from '../../../../../store/deliveryFlow/slices';

const IconMap = getImageURL('/icon/icon-map.svg');

const MapContainer = styled.div(({ theme }) => ({
  marginTop: theme.isMobile ? 12 : 16,
  borderRadius: 8,
  ...theme.isMobile && {
    marginTop: 12,
  },
}));

const SubmitButton = styled(Button)(({ theme, disabled }) => ({
  ...theme.radius.m,
  fontSize: '18px',
  lineHeight: 28 / 18,
  fontWeight: 700,
  padding: '6px 12px',
  minWidth: theme.isMobile ? 'unset' : 160,
  width: theme.isMobile ? '100%' : 'auto',
  textTransform: 'none',
  backgroundColor: disabled ? theme.colors.green100 : theme.colors.green600,
  color: disabled ? theme.colors.grey500 : theme.colors.white,
  '&:hover': {
    backgroundColor: theme.colors.green400,
  },
}));

const Icon = styled.img(({ w, h }) => ({
  width: w,
  height: h,
}));

const BtnIcon = styled(IconButton)(({
  w = 24, h = 24, ml, mb, mr, flexGrow
}) => ({
  width: w,
  height: h,
  borderRadius: 40,
  padding: '8px 16px 8px 16px',
  backgroundColor: '#F3F3F3',
  display: 'flex'
}));

const Content = styled.div(() => ({
  width: '100%',
  margin: 'auto',
}));

const FlexRow = styled(Box)(({ width, margin, justifyContent }) => ({
  height: 44,
  display: 'flex',
  flexDirection: 'row',
  width,
  margin,
  justifyContent: justifyContent ?? 'left',
  alignContent: 'center',
}));

const useStyle = makeStyles((theme) => ({
  footer: {
    position: 'relative',
    bottom: 0,
    left: 0,
    backgroundColor: 'white',
    marginLeft: 'auto', 
    marginRight: 'auto', 
    zIndex: 10,
    paddingTop: 24,
    [theme.breakpoints.only('xs')]: {
      paddingTop: 12,  
      paddingBottom: 0,
      width: 'auto',
      left: 0,
      right: 0,
      margin: 'auto'
    }
  },
  btnFooter: {
    position: 'relative',
    width: '100%',
    backgroundColor: 'white',
    zIndex: 10,
    paddingTop: theme.isMobile ? 12 : 22,
    paddingBottom: theme.isMobile ? 0 : 24,
  },
  submitButton: {
    height: 'auto !important',
    maxHeight: '44px !important'
  },
  confirmButton: {
    height: 'auto !important',
    maxHeight: '44px !important'
  },
  backButton: {
    marginRight: '10px',
    fontSize: '18px',
    fontFamily: 'LotusSmartHL',
    textTransform: 'none',
    padding: '8px 46px',
    borderRadius: '8px',
    width: '100%',
    backgroundColor: '#E8E8E8',
    color: '#424242',
  },
  containerMapHeight: {
    height: 'auto !important',
    minHeight: ({ contentMinHeight }) => contentMinHeight,
    padding: 'unset',
    paddingTop: 12,
  },
  containerFormHeight: {
    height: 'auto !important',
    padding: 'unset',
    paddingTop: 12,
  },
  contentContainer: {
    padding: 0,
    minHeight: ({ contentMinHeight }) => contentMinHeight,
    paddingTop: 16,
  },
  warningIcon: {
    marginTop: 8,
    width: 20,
    color: '#F9BE00',
    [theme.breakpoints.down('xs')]: {
      marginTop: 12,
    }
  },
  addressWarningHeader: {
    ...theme.fonts.h6,
    fontSize: 14,
    fontWeight: 700,
  },
  addressWarning: {
    background: theme.custom.colors.yellow100,
    borderRadius: 12,
    padding: 14,
    paddingTop: 0,
    paddingBottom: 0,
    marginBottom: 8,
  },
  warningText: {
    ...theme.fonts.textM,
    fontWeight: 500,
    fontSize: 14,
    color: theme.custom.colors.grey550,
  }
}));

export default function DeliveryAddress({
  onNext,
  onBack,
  onClose,
  modalRef
}) {
  const dispatch = useDispatch();
  const { isMobile, isTablet } = useResponsive();
  const classes = useStyle({ contentMinHeight: defaultModalSize.mobile.contentMinHeight });
  const currentLanguage = useSelector((state) => state.currentLanguage);
  const deliveryData = useDeliveryData();
  const deliveryControl = useDeliveryControlFlow();
  const [enableGeo, setEnableGeo] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const word = useLanguage();
  const { MySite, ThSite } = useSite();
  const timeoutRef = React.useRef();
  const [address, setAddress] = useState('');
  const [searchState, setSearchState] = useState(false);
  const [latLng, setLatLng] = useState({
    lat: deliveryData.shippingAddress.latitude,
    lng: deliveryData.shippingAddress.longitude,
  });

  const [placeName, setPlaceName] = useState(deliveryData.shippingAddress.placeName);
  const [addressNumber, setAddressNumber] = useState('');
  const [thSubDistrict, setThSubDistrict] = useState('');
  const [thDistrict, setThDistrict] = useState('');
  const [thProvince, setThProvince] = useState('');
  const [mySubDistrict, setMySubDistrict] = useState('');
  const [myDistrict, setMyDistrict] = useState('');
  const [myCity, setMyCity] = useState('');
  const [myState, setMyState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [disable, setDisable] = useState(true);
  const [disablePostalCode, setDisablePostalCode] = useState(true);
  const [disableCity, setDisableCity] = useState(true);
  const [disableMyState, setDisableMyState] = useState(true);
  const [disableScrollY, setDisableScrollY] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [step, setStep] = useState(1);
  const [disableConfirmDestination, setDisableConfirmDestination] = useState(false);
  const isTHLang = currentLanguage.language === 'th';

  const validate = (() => {
    if (configs.siteCode === 'TH') {
      return (
        addressNumber !== ''
        && thSubDistrict !== ''
        && thDistrict !== ''
        && thProvince !== ''
        && postalCode !== ''
      );
    }
    if (configs.siteCode === 'MY') {
      return (
        addressNumber !== ''
        && mySubDistrict !== ''
        && myCity !== ''
        && myState !== ''
        && postalCode !== ''
      );
    }

    return false;
  })();

  const setDefaultLocation = () => {
    setDisableConfirmDestination(true);
    if (ThSite) {
      setLatLng({
        lat: 13.745713,
        lng: 100.5336414,
      });
    } else {
      setLatLng({
        lat: 3.1386299225989105,
        lng: 101.68574698006697,
      });
    }
  };

  if (navigator.geolocation && navigator.permissions) {
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (result.state === 'denied') {
          setDisable(true);
          setEnableGeo(false);
        } else if (result.state === 'granted') {
          setDisable(true);
          setEnableGeo(true);
        }

        result.onchange = () => {
          if (result.state === 'denied') {
            deliveryControl.setLoading(false);
            setDefaultLocation();
            setDisable(true);
            setEnableGeo(false);
          } else if (result.state === 'granted') {
            setDisable(true);
            setEnableGeo(true);
          }
        };
      });
  }

  const onNextFunc = () => {
    let data = {
      placeName,
      addressNumber,
      postalCode,
      latitude: latLng.lat,
      longitude: latLng.lng,
    };
    if (MySite) {
      data = {
        ...data,
        mySubDistrict,
        myDistrict,
        myCity,
        myState,
      };
    } else {
      data = {
        ...data,
        thSubDistrict,
        thDistrict,
        thProvince,
      };
    }
    onNext(data);
  };

  const fillAddressForm = (address) => {
    setAddressNumber(_.get(address, 'addressNumber', ''));
    setPlaceName(address.placeName);
    setThSubDistrict(_.get(address, 'thSubDistrict', ''));
    setThDistrict(_.get(address, 'thDistrict', ''));
    setThProvince(_.get(address, 'thProvince', ''));
    setMySubDistrict(_.get(address, 'mySubDistrict', ''));
    setMyCity(_.get(address, 'myCity', ''));
    setMyState(_.get(address, 'myState', ''));
    setPostalCode(address.postalCode);
    setMyDistrict(_.get(address, 'myDistrict', ''));
  };

  const setAddressFromMap = async (latLng, address) => {
    if (searchState) {
      setSearchState(false);
      return;
    }
    setLatLng(latLng);
    deliveryControl.setLoading(false);
    if (address === undefined) {
      setDefaultLocation();
    }
    setDisable(true);
    fillAddressForm(address);
    setDisablePostalCode(!!address?.postalCode);
    setDisableCity(!!address?.myCity);
    setDisableMyState(!!address?.myState);
  };

  useEffect(() => {
    const { shippingAddress } = deliveryData;
    if (shippingAddress.addressNumber !== undefined || shippingAddress.latitude || shippingAddress.longitude) {
      deliveryControl.setLoading(true);
      setDisable(true);
      setLatLng({
        lat: shippingAddress.latitude,
        lng: shippingAddress.longitude,
      });
      setAddressNumber(shippingAddress.addressNumber);
      setThSubDistrict(shippingAddress.thSubDistrict);
      setThDistrict(shippingAddress.thDistrict);
      setThProvince(shippingAddress.thProvince);
      setMySubDistrict(shippingAddress.mySubDistrict ?? '');
      setMyDistrict(shippingAddress.myDistrict ?? '');
      setMyCity(shippingAddress.myCity ?? '');
      setMyState(shippingAddress.myState ?? '');
      setPostalCode(shippingAddress.postalCode);
    }
    return () => deliveryControl.setLoading(false);
  }, [deliveryData.shippingAddress]);

  useEffect(() => {
    if (enableGeo === false) {
      setShowHint(true);
      setDefaultLocation();
      deliveryControl.setLoading(false);
      setTimeout(() => {
        setShowHint(false);
      }, 3000);
    }
  }, [enableGeo]);

  useEffect(() => {
    if (deliveryControl.isProcess) {
      timeoutRef.current = setTimeout(() => {
        deliveryControl.setLoading(false);
        setDefaultLocation();
      }, 5000);
    } else {
      clearTimeout(timeoutRef.current);
    }
  }, [deliveryControl.isProcess]);

  useEffect(() => {
    trackPageLoad('Delivery Map Selection');
    deliveryControl.setLoading(true);
    return () => deliveryControl.setLoading(false);
  }, []);

  const handleChange = (address) => {
    setAddress(address);
    setIsSearch(Boolean(address));
    setSearchState(Boolean(address));
  };

  const handleSelect = (address) => {
    setPlaceName(address.split(',')[0]);
    geocodeByAddress(address)
      .then(async (results) => {
        const data = await getLatLng(results[0]);
        setAddress(address);
        const [addressMap, completedAddress] = interpretAddress(data.lat, data.lng, results[0]);
        fillAddressForm(completedAddress);
        setDisablePostalCode(!!completedAddress?.postalCode);
        setDisableCity(!!completedAddress?.myCity);
        setDisableMyState(!!completedAddress?.myState);
        return data;
      })
      .then((latLng) => {
        setLatLng(latLng);
        setIsSearch(false);
        setDisableConfirmDestination(false);
        return latLng;
      });
  };

  const handlerSearchError = (status) => {
    setDisableScrollY(Boolean(status));
  };

  const handleChangeValue = (e) => {
    const re = /^[\u0E00-\u0E7Fa-zA-Z0-9- ,.()/']+$/;
    if (e.target.value === '' || re.test(e.target.value)) {
      if (e.target.name === 'addressNumber') {
        setAddressNumber(e.target.value);
        setPlaceName(e.target.value);
      } else if (e.target.name === 'mySubDistrict') {
        setMySubDistrict(e.target.value);
      } else if (e.target.name === 'thSubDistrict') {
        setThSubDistrict(e.target.value);
      } else if (e.target.name === 'thDistrict') {
        setThDistrict(e.target.value);
      } else if (e.target.name === 'postalCode') {
        setPostalCode(e.target.value);
      } else if (e.target.name === 'myDistrict') {
        setMyCity(e.target.value);
      } else if (e.target.name === 'myProvince') {
        setMyState(e.target.value);
      }
    }
  };

  const contentContainerRef = useRef(null);
  const getHeight = () => {
    if (step === 2) {
      if (window.innerHeight < 1080) {
        return isTHLang ? '511px' : '529px';
      }
      return 'auto';
    }
    return '100%';
  };

  const getInfoAddress = () => {
    if (step === 1) {
      return word('DeliveryAddress.Maps.Message');
    }
    return word('DeliveryAddress.TextBox.Message');
  };

  const getClassName = () => {
    if (isMobile) {
      if (step === 1) {
        return classes.containerMapHeight;
      }
      return classes.containerFormHeight;
    }
    if (isTablet) {
      return classes.contentContainer;
    }
    if (step === 1) {
      return classes.contentContainer;
    }
    return classes.contentContainer;
  };

  const getMapHeightWithMobileSize = () => {
    if (window.innerHeight < 606) {
      return 275;
    }
    return 320;
  };

  useEffect(() => {
    if (deliveryData?.deliveryAddressStep) {
      setStep(deliveryData?.deliveryAddressStep);
    }
  }, [deliveryData?.deliveryAddressStep]);

  return (
    <Layout
      title={word('DeliveryAddress.Title')}
      backButtonShown={deliveryControl.stackSize > 1}
      description={word('DeliveryAddress.Description')}
      onBack={() => {
        if (step === 2) {
          setStep(step - 1);
        } else {
          onBack();
        }
      }}
      onClose={() => {
        onClose();
        dispatch(setDeliveryAddressStep(null));
      }}
      width={isTablet ? defaultModalSize.tablet.width : defaultModalSize.desktop.width}
      height={getHeight()}
      scrollY={!disableScrollY}
      bottomSpace={0}
      infoAddress={getInfoAddress()}
      containerHeight={step === 1 ? '100%' : 'auto'}
      containerOverflowY={'hidden'}
      paddingTop={0}
      paddingBottom={0}
    >
      <Container className={getClassName()} ref={contentContainerRef}>
        <Main maxHeight={'auto'}>
          <Content>
            {(step === 1) && (
              <PlacesSearch
                value={address}
                onSearch={handleChange}
                onSelect={handleSelect}
                onError={handlerSearchError}
                floating
                showHint={showHint}
              />
            )}
            <Fade in={!isSearch}>
              <div>
                {!isSearch && (
                  <div id="fade-container">
                    {(step === 1) && (
                      <>
                        <MapContainer>
                          <Map
                            onMoveLocation={() => setDisableConfirmDestination(false)}
                            latLng={latLng}
                            height={isMobile ? getMapHeightWithMobileSize() : 303}
                            onLocationChange={(latLng, address) => {
                              setAddressFromMap(latLng, address);
                            }}
                          />
                        </MapContainer>
                        <FlexRow
                          justifyContent="center"
                          className={classes.btnFooter}
                        >
                          <SubmitButton
                            full
                            disabled={disableConfirmDestination}
                            className={classes.confirmButton}
                            onClick={() => setStep(step + 1)}
                            id="confirm-destination-address"
                            data-cta-name-en="Confirm Destination"
                            data-cta-name-th="เลือกจุดนี้"
                          >
                            {word('DeliveryAddress.Maps.Select')}
                          </SubmitButton>
                        </FlexRow>
                      </>
                    )}
                    {(step === 2) && (
                      <Box
                        style={{ maxWidth: '100%', paddingBottom: 0 }}
                      >
                        <div style={{ paddingBottom: isMobile ? 12 : 16, display: 'flex', justifyContent: 'flex-end' }}>
                          <BtnIcon w={90} h={36} id="map-icon" onClick={() => setStep(step - 1)}>
                            <Icon w="20px !important" h={20} src={IconMap} />
                            <div style={{ fontSize: 14, paddingLeft: 4, color: '#424242' }}>{word('DeliveryAddress.Maps')}</div>
                          </BtnIcon>
                        </div>
                        { ThSite && (
                          <ThailandAddressForm
                            onAddressNumberChange={handleChangeValue}
                            disable={disable}
                            disablePostalCode={disablePostalCode}
                            addressNumber={addressNumber}
                            thSubDistrict={thSubDistrict}
                            thDistrict={thDistrict}
                            thProvince={thProvince}
                            postalCode={postalCode}
                          />
                        )}
                        { MySite && (
                          <MalaysiaAddressForm
                            onAddressNumberChange={handleChangeValue}
                            disable={disable}
                            disablePostalCode={disablePostalCode}
                            disableCity={disableCity}
                            disableState={disableMyState}
                            addressNumber={addressNumber}
                            mySubDistrict={mySubDistrict}
                            myCity={myCity}
                            myState={myState}
                            postalCode={postalCode}
                          />
                        )}
                        <FlexRow
                          justifyContent="center"
                          className={classes.btnFooter}
                        >
                          <SubmitButton
                            disabled={!validate}
                            className={classes.submitButton}
                            onClick={onNextFunc}
                            id="confirm-address-btn"
                          >
                            {word('General.Confirm')}
                          </SubmitButton>
                        </FlexRow>
                      </Box>
                    )}
                  </div>
                )}
              </div>
            </Fade>
          </Content>
        </Main>
      </Container>
    </Layout>
  );
}
